import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaPlay, FaSpinner, FaHistory, FaCheck, FaTimes, FaClock } from 'react-icons/fa';

const BuildManagement = () => {
  const [isBuilding, setIsBuilding] = useState(false);
  const [buildOutput, setBuildOutput] = useState('');
  const [buildHistory, setBuildHistory] = useState([]);
  const [currentBuild, setCurrentBuild] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const startBuild = async (env) => {
    try {
      setIsBuilding(true);
      setBuildOutput(`Starting ${env} build...\n`);
      
      const response = await axios.post(`http://localhost:5000/api/build/${env}`);
      
      if (response.data.success) {
        alert(`Build iniciado para el entorno ${env}`);
        // Start polling for build status
        pollBuildStatus();
      } else {
        throw new Error(response.data.message || 'Failed to start build');
      }
    } catch (error) {
      console.error('Build error:', error);
      alert(`Error: ${error.message || 'Failed to start build'}`);
      setIsBuilding(false);
    }
  };

  const pollBuildStatus = async () => {
    try {
      const response = await axios.get('http://localhost:5000/api/build/status');
      
      if (response.data.output) {
        setBuildOutput(response.data.output.join(''));
      }
      
      if (response.data.currentBuild) {
        setCurrentBuild(response.data.currentBuild);
      }
      
      if (response.data.isBuilding) {
        // Continue polling if build is still in progress
        setTimeout(pollBuildStatus, 1000);
      } else {
        setIsBuilding(false);
        setCurrentBuild(null);
        // Refresh build history when build completes
        fetchBuildHistory();
        
        // Check if build was successful based on output
        const lastOutput = response.data.output[response.data.output.length - 1] || '';
        if (lastOutput.includes('Build completed successfully')) {
          alert('¡Build completado exitosamente!');
        } else if (lastOutput.includes('Build failed')) {
          alert('Build falló! Revisa la salida para más detalles.');
        }
      }
    } catch (error) {
      console.error('Error polling build status:', error);
      setIsBuilding(false);
      setCurrentBuild(null);
      alert('Error verificando el estado del build');
    }
  };

  const fetchBuildHistory = async () => {
    try {
      const response = await axios.get('http://localhost:5000/api/build/history');
      if (response.data.success) {
        setBuildHistory(response.data.history);
      }
    } catch (error) {
      console.error('Error fetching build history:', error);
    }
  };

  const formatDuration = (ms) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    }
    return `${seconds}s`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  // Load build history on component mount
  useEffect(() => {
    fetchBuildHistory();
  }, []);

  return (
    <div className="container mt-5">
      <h2>Build Management</h2>
      <p className="text-muted mb-4">Trigger builds for different environments</p>
      
      <div className="row mb-4">
        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              <h5 className="card-title">Build de Producción</h5>
              <p className="card-text">Construye la aplicación para el entorno de producción con optimizaciones completas.</p>
              <button 
                className="btn btn-danger"
                onClick={() => startBuild('production')}
                disabled={isBuilding}
              >
                {isBuilding ? (
                  <>
                    <FaSpinner className="spinner-border spinner-border-sm me-2" />
                    Building...
                  </>
                ) : (
                  <>
                    <FaPlay className="me-2" />
                    Build Production
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
        
        <div className="col-md-6">
          <div className="card">
            <div className="card-body">
              <h5 className="card-title">Build de QA</h5>
              <p className="card-text">Construye la aplicación para el entorno de pruebas (QA) con configuraciones de desarrollo.</p>
              <button 
                className="btn btn-warning"
                onClick={() => startBuild('qa')}
                disabled={isBuilding}
              >
                {isBuilding ? (
                  <>
                    <FaSpinner className="spinner-border spinner-border-sm me-2" />
                    Building...
                  </>
                ) : (
                  <>
                    <FaPlay className="me-2" />
                    Build QA
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Current Build Status */}
      {currentBuild && (
        <div className="alert alert-info mb-4">
          <FaClock className="me-2" />
          Currently building <strong>{currentBuild.env}</strong> environment 
          (Started: {formatDate(currentBuild.startTime)})
        </div>
      )}

      {/* Build History Toggle */}
      <div className="mb-3">
        <button 
          className="btn btn-outline-secondary"
          onClick={() => setShowHistory(!showHistory)}
        >
          <FaHistory className="me-2" />
          {showHistory ? 'Hide' : 'Show'} Build History
        </button>
      </div>

      {/* Build History */}
      {showHistory && (
        <div className="card mb-4">
          <div className="card-header">
            <h5 className="mb-0">Build History</h5>
          </div>
          <div className="card-body p-0">
            {buildHistory.length === 0 ? (
              <div className="p-3 text-muted">No builds found</div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Status</th>
                      <th>Environment</th>
                      <th>Start Time</th>
                      <th>Duration</th>
                      <th>Build ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {buildHistory.map((build) => (
                      <tr key={build.id}>
                        <td>
                          {build.success ? (
                            <span className="badge bg-success">
                              <FaCheck className="me-1" />
                              Success
                            </span>
                          ) : (
                            <span className="badge bg-danger">
                              <FaTimes className="me-1" />
                              Failed
                            </span>
                          )}
                        </td>
                        <td>
                          <span className={`badge ${build.env === 'production' ? 'bg-danger' : 'bg-warning'}`}>
                            {build.env.toUpperCase()}
                          </span>
                        </td>
                        <td>{formatDate(build.startTime)}</td>
                        <td>{formatDuration(build.duration)}</td>
                        <td>
                          <code>{build.id}</code>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Build Output */}
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <span>Build Output</span>
          {isBuilding && (
            <span className="badge bg-primary">
              <FaSpinner className="spinner-border spinner-border-sm me-1" />
              Building...
            </span>
          )}
        </div>
        <div className="card-body p-0">
          <pre className="bg-dark text-light p-3 mb-0" style={{ 
            minHeight: '300px', 
            maxHeight: '500px', 
            overflowY: 'auto',
            fontSize: '12px',
            lineHeight: '1.4'
          }}>
            {buildOutput || 'Build output will appear here...'}
          </pre>
        </div>
      </div>
    </div>
  );
};

export default BuildManagement;
